import React, { useState } from "react";
import { Button, Col, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap";
import { toast } from "react-toastify";
import { sendAddendum } from "./api";

/**
 * "Send for signing" dialog. The manager confirms WHERE the addendum goes via an
 * editable recipient repeater (pre-filled from the unit's tenants). Each row is a
 * signer who must e-sign — mirroring the lease flow where every tenant signs.
 * On send we persist the recipients onto the addendum's signature slots, then
 * email each a secure signing link.
 */
const AddendumSendModal = ({ isOpen, toggle, addendum, onSent }) => {
  const [rows, setRows] = useState(() => {
    const sigs = (addendum?.tenant_signatures || []).map((t) => ({
      name: t.name || "",
      email: t.email || "",
      signed: !!t.signed,
      signature: t.signature || "",
      date: t.date || "",
    }));
    return sigs.length ? sigs : [{ name: "", email: "", signed: false }];
  });
  const [sending, setSending] = useState(false);

  const setRow = (i, key, val) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [key]: val } : row)));
  const addRow = () => setRows((r) => [...r, { name: "", email: "", signed: false }]);
  const removeRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));

  const handleSend = async () => {
    const valid = rows.filter((r) => r.email && r.email.includes("@"));
    if (!valid.length) {
      toast.error("Add at least one recipient with a valid email");
      return;
    }
    setSending(true);
    try {
      const tenant_signatures = rows.map((r, idx) => ({
        tenant_index: idx,
        name: r.name || "",
        email: r.email || "",
        signed: !!r.signed,
        signature: r.signature || "",
        date: r.date || "",
      }));
      // Pass tenant_signatures directly to send — the server persists them
      // and sends links in one step, so no separate PATCH is needed.
      const res = await sendAddendum(addendum._id, { tenant_signatures });
      toast.success(res.data?.message || "Signing links sent");
      onSent && onSent();
      toggle();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle}>Send addendum for signing</ModalHeader>
      <ModalBody>
        <p className="text-muted" style={{ fontSize: 13 }}>
          Your manager signature is applied automatically, so each recipient receives an already manager-signed document. Every listed signer must sign before it can be marked completed.
        </p>
        {rows.map((row, i) => {
          // Tenants are authored on the addendum itself, so this is a confirm step.
          // Only a missing or malformed email opens up for editing here.
          const needsEmail = !row.email || !row.email.includes("@");
          return (
            <Row key={i} className="align-items-end mb-2 g-2">
              <Col xs={1} className="px-0 text-center">
                <span className="text-muted" style={{ fontSize: 12, fontWeight: 600 }}>{i + 1}</span>
              </Col>
              <Col xs={5}>
                {needsEmail ? (
                  <>
                    <Label className="mb-1" style={{ fontSize: 12 }}>Name</Label>
                    <Input bsSize="sm" value={row.name} onChange={(e) => setRow(i, "name", e.target.value)} placeholder="Tenant name" />
                  </>
                ) : (
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{row.name || "Tenant"}</div>
                )}
              </Col>
              <Col xs={5}>
                {needsEmail ? (
                  <>
                    <Label className="mb-1" style={{ fontSize: 12 }}>Email</Label>
                    <Input bsSize="sm" type="email" value={row.email} onChange={(e) => setRow(i, "email", e.target.value)} placeholder="name@example.com" invalid />
                  </>
                ) : (
                  <div className="text-muted" style={{ fontSize: 13 }}>{row.email}</div>
                )}
              </Col>
              <Col xs={1} className="px-0 text-center">
                {row.signed ? (
                  <span className="text-success" title="Already signed">✓</span>
                ) : (
                  needsEmail && rows.length > 1 && (
                    <Button color="link" className="text-danger p-0" title="Remove" onClick={() => removeRow(i)}>
                      ✕
                    </Button>
                  )
                )}
              </Col>
              {needsEmail && (
                <Col xs={12}>
                  <small className="text-danger">An email is needed before this tenant can be sent a signing link.</small>
                </Col>
              )}
            </Row>
          );
        })}
        <Button color="link" className="p-0" onClick={addRow}>
          + Add recipient
        </Button>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={toggle} disabled={sending}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleSend} disabled={sending}>
          {sending ? "Sending…" : "Send signing links"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AddendumSendModal;
